import { useQuery } from '@tanstack/react-query';

import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import type { PaginationVariables } from '~settings/api/types/paginationVariables';

import type { UserTypeMap } from '../../constants/UserTypeMap';
import { RoleQueryKeys } from '../config/queryKeys';
import { BFFClient } from '../lib/BFFClient';
import {
  type UserTypeListResponse,
  UserTypeListResponseSchema,
} from '../lib/schemas';
import type { PresetUseQueryOptions } from '../lib/types';

export interface UserTypesQueryVariables extends PaginationVariables {
  targetUserType?: keyof typeof UserTypeMap;
}

const OLD_USER_TYPES: UserTypeListResponse = {
  total: 2,
  page: 1,
  totalPages: 1,
  data: [
    { id: 'internal', name: 'INTERNAL' },
    { id: 'external', name: 'EXTERNAL' },
  ],
};

export function useUserTypes<TData extends UserTypeListResponse>(
  variables: UserTypesQueryVariables,
  options?: PresetUseQueryOptions<UserTypeListResponse, TData>,
) {
  const [isInternalUserUpdatesEnabled] = useFeatureFlags([
    FEATURE_FLAGS.SettingsInternalUserUpdates,
  ]);

  if (variables.targetUserType === undefined) {
    delete variables.targetUserType;
  }

  const { data: userTypes, ...query } = useQuery({
    queryKey: RoleQueryKeys.types(variables),
    queryFn: async () => {
      if (!isInternalUserUpdatesEnabled) {
        return UserTypeListResponseSchema.parse(OLD_USER_TYPES);
      }

      const responseData = await BFFClient.get('user-types', {
        searchParams: { ...variables },
      }).json();

      return UserTypeListResponseSchema.parse(responseData);
    },
    ...options,
  });

  return { userTypes, ...query };
}
